/* eslint-disable react/require-default-props */
import clsx from 'clsx';
import React, {
  forwardRef,
  FunctionComponent,
  memo,
  ReactNode,
  useEffect,
} from 'react';

import { ITableOptions } from '.';

interface IProps {
  options?: ITableOptions | null;
  children: ReactNode;
  containerClassName?: string;
  columnLength: number;
}

// This is the same code, but extracted out to be reused elsewhere
const TableContainer: FunctionComponent<IProps> = forwardRef<
  HTMLDivElement,
  IProps
>(
  (
    {
      children,
      containerClassName = '',
      options = undefined,
      columnLength,
    }: IProps,
    ref
  ) => {
    // Error checking to make sure devs enter correct columns
    useEffect(() => {
      if (!options?.colWidths) return;

      const { colWidths } = options;

      if (colWidths.length !== columnLength) {
        throw Error(
          "The length of 'colWidths' needs to match the length of 'columns'"
        );
      }

      const totalColWidth = colWidths.reduce((prev, curr) => prev + curr, 0);

      if (totalColWidth > 1) {
        throw Error(
          `'colWidths' cannot be greater than '1. It currently adds up to ${totalColWidth}`
        );
      }
    }, [columnLength, options]);

    const renderColGroup = () => {
      if (options && options.colWidths) {
        return (
          <colgroup>
            {options.colWidths.map((item, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <col key={i} style={{ width: `${item * 100}%` }} />
            ))}
          </colgroup>
        );
      }

      return null;
    };

    return (
      <div
        ref={ref}
        className={clsx('rounded border-1 border-gray-10', containerClassName)}
      >
        <table
          className={clsx(
            options && options.colWidths && 'table-fixed',
            ' relative w-full border-collapse rounded-none text-gray-100'
          )}
        >
          {renderColGroup()}
          {children}
        </table>
      </div>
    );
  }
);

export default memo(TableContainer);
